import React from "react";
import { Button } from "../Button";
import EntryLiveFilters from "./EntryLiveFilters";
import { ISearchContext, SearchContextProvider, useSearch } from "./SearchContext";

export enum EEntrySearchLayout {
  row1 = "row1",
  row2 = "row2",
  col5row2 = "col5row2",
}

export interface IEntrySearchProps {
  title?: string;
  /**
   * classes for the title, a legend element
   */
  titleClassName?: string;
  /**
   * Classes to add to the search fieldset element.
   */
  className?: string;
  /**
   * Classes to add to the form controls container
   */
  controlsBoxClassName?: string;
  /**
   * Filter nodes to display in the standard filters section of the search.
   */
  standardFilters?: (context: ISearchContext) => React.ReactNode;

  layout?: EEntrySearchLayout;
  /**
   *
   */

  /**
   * Filter nodes to display in the OR section of entry search.
   */
  orFilters?: (context: ISearchContext) => React.ReactNode;

  /**
   * Theme the button specifically
   */
  buttonClassName?: string;

  /**
   * Display button inline or on it's own line
   */
  buttonInline?: boolean;

  /**
   * Renderable component if any for the show advanced link
   */
  showAdvancedComponent?: React.FC;

  onSubmit?: (filters: {}) => void;

  buttonText?: string | React.ReactNode;
}

const SearchButton: React.FC<{
  onSubmit?: (filters: {}) => void;
  buttonClassName?: string;
  buttonText?: string | React.ReactNode;
}> = ({ onSubmit, buttonClassName, buttonText = "Search" }) => {
  const context = useSearch();
  return (
    <Button
      linkProps={{
        className: `px-5 py-2 min-w-[150px] ${buttonClassName}`,
        onClick: (event) => {
          onSubmit && onSubmit(context.currentFilterValues || {});
          return false;
        },
      }}
    >
      {buttonText}
    </Button>
  );
};

export const EntrySearch: React.FC<IEntrySearchProps> = ({
  className,
  controlsBoxClassName = "",
  title,
  layout = EEntrySearchLayout.row1,
  titleClassName = "text-3xl",
  standardFilters,
  orFilters,
  buttonClassName,
  buttonInline = true,
  showAdvancedComponent,
  onSubmit,
  buttonText,
}) => {
  let flexBoxClass = layout === EEntrySearchLayout.col5row2 ? "lg:flex-nowrap" : "md:flex-nowrap";
  let flexBoxColWidth = layout === EEntrySearchLayout.col5row2 ? "lg:w-auto" : "md:w-auto";
  let btnContainerSizeClass = layout === EEntrySearchLayout.col5row2 ? "lg:w-auto" : "md:w-auto";

  let fieldColClass: string;
  switch (layout) {
    case EEntrySearchLayout.col5row2:
      fieldColClass = "lg:grid-cols-5 lg:grid-flow-col lg:grid-rows-2";
      break;
    case EEntrySearchLayout.row2:
      fieldColClass = "md:grid-cols-4 md:grid-flow-col md:grid-rows-2";
      break;
    default:
      fieldColClass = "md:grid-cols-4";
      break;
  }
  return (
    <SearchContextProvider>
      <fieldset className={`p-2 ${className}`}>
        {title && (
          <legend className={`font-bold ${titleClassName}`}>{title}</legend>
        )}
        <div
          className={`entry-search flex flex-wrap ${buttonInline ? flexBoxClass : "justify-center gap-3"
            } ${controlsBoxClassName}`}
        >
          <EntryLiveFilters
            flexBoxColWidth={flexBoxColWidth}
            fieldColClass={fieldColClass}
            standardFilters={standardFilters}
            orFilters={orFilters}
          />
          <div className={`entry-search-submit w-full ${btnContainerSizeClass} mx-3 flex flex-col items-center justify-center`}>
            <SearchButton
              onSubmit={onSubmit}
              buttonClassName={buttonClassName}
              buttonText={buttonText}
            />
            {showAdvancedComponent && showAdvancedComponent({})}
          </div>
        </div>
      </fieldset>
    </SearchContextProvider>
  );
};

export default EntrySearch;
